import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LandingHeader } from './LandingHeader';
import { initialUserEarlyAccessResponse, UserEarlyAccessResponse } from '../Types/EarlyAccessType';
import { createEarlyAccess } from '../Service/EarlyAccessService';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { getEmailValidationError } from '../../Shared/Utils/validationPolicy';
import { useValidationPolicy } from '../../Shared/Hooks/useValidationPolicy';
import LandingFooter from './LandingFooter';
import { EarlyAccessHeader } from './EarlyAccessHeader';

// Define Types for Form States
interface BaseUser {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  role: string;
  linkedInUrl: string;
}

interface CandidateData extends BaseUser {}

interface RecruiterData extends BaseUser {
  annualLeadershipHiringVolume: string;
}

type UserType = 'candidate' | 'recruiter';

export const EarlyRegistrationForm: React.FC = () => {
  const location = useLocation();
  const requestedType = (location.state as { userType?: UserType } | null)?.userType;
  const [userType, setUserType] = useState<UserType>(requestedType === 'recruiter' ? 'recruiter' : 'candidate');

  // Unified State for simplicity
  const [formData, setFormData] = useState<RecruiterData>({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    role: '',
    linkedInUrl: '',
    annualLeadershipHiringVolume: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const [adminData, setAdminData] = useState<UserEarlyAccessResponse | null>(initialUserEarlyAccessResponse);
  

  // const handleSubmit = (e: React.FormEvent) => {
  //   e.preventDefault();
    
  //   // Filter payload based on user type before submission
  //   if (userType === 'candidate') {
  //     const { annualLeadershipHiringVolume, ...candidatePayload } = formData;
  //     console.log('Submitting Candidate Data:', candidatePayload);
  
  //   } else {
  //     console.log('Submitting Recruiter Data:', formData);
  //   }
  // };

  const [adminData, setAdminData] = useState<UserEarlyAccessResponse | null>(null);
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const { policy } = useValidationPolicy();


const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  const emailError = getEmailValidationError(formData.email, policy);
  if (emailError) {
    setAlert({
      variant: "error",
      title: "Invalid Email",
      message: emailError,
      autoCloseMs: 5000,
      onClose: () => setAlert(null),
    });
    return;
  }

  try {
    if (userType === "candidate") {
      const { annualLeadershipHiringVolume, ...candidatePayload } = formData;
      const payload = {
        ...candidatePayload,
        platformRole: "CANDIDATE",
      };

      const response = await createEarlyAccess(payload);

      if (response.success) {
        setAdminData(response.data);

        setAlert({
          variant: "success",
          title: "Success...!",
          message: response.message,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      } else {
        setAlert({
          variant: "error",
          title: "Registration Error",
          message: response.message,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } else {
      const payload = {
        ...formData,
        platformRole: "RECRUITER",
      };
      const response = await createEarlyAccess(payload);

      if (response.success) {
        setAdminData(response.data);

        setAlert({
          variant: "success",
          title: "Success...!",
          message: response.message,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }else {
        setAlert({
          variant: "error",
          title: "Registration Error",
          message: response.message,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    }
  } catch (error) {
    console.error(error);

    setAlert({
      variant: "error",
      title: "Error",
      message: "Something went wrong. Please try again.",
      autoCloseMs: 5000,
      onClose: () => setAlert(null),
    });
  }
};

  return (
    <>
      <div className="bg-slate-950 text-slate-100">
      <LandingHeader/>

      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6 py-12">
        <div className="w-full max-w-2xl flex flex-col gap-6">

          {/* Early Access Header Component Rendered Cleanly Above Form */}
          {/* <EarlyAccessHeader /> */}

          {/* Main Registration Form Card */}
          <div className="w-full bg-slate-900 border border-slate-800 rounded-xl p-8 shadow-2xl shadow-cyan-950/20 relative overflow-hidden">

            {alert && (
              <div className="mb-6">
                <Alert {...alert} onClose={() => setAlert(null)} />
              </div>
            )}

            {/* Cyber Neon Accent Bar */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />

            {/* Form Section Header */}
            <div className="mb-8">
              {/* The only h1 on the early access page. */}
              <h1 className="text-2xl font-bold tracking-wider text-slate-200 uppercase">
                 <span className="text-cyan-400 font-mono text-xl tracking-normal">Pre-Launch Early Access Registration</span>
              </h1>
              <p className="text-xs text-slate-400 mt-1 font-mono">SELECT IDENTITY CONTEXT TO INITIALIZE</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Radio Group Selection */}
              <div className="bg-slate-950/50 border border-slate-800 p-4 rounded-lg flex gap-6">
                <label className="flex items-center gap-3 cursor-pointer group select-none">
                  <div className="relative flex items-center justify-center">
                    <input
                      type="radio"
                      name="userType"
                      value="candidate"
                      checked={userType === 'candidate'}
                      onChange={() => setUserType('candidate')}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border transition-all duration-200 ${
                      userType === 'candidate' 
                        ? 'border-cyan-400 bg-cyan-950/30' 
                        : 'border-slate-700 bg-slate-900 group-hover:border-slate-500'
                    }`} />
                    {userType === 'candidate' && (
                      <div className="absolute w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_#22d3ee]" />
                    )}
                  </div>
                  <span className={`text-sm font-medium tracking-wide transition-colors ${
                    userType === 'candidate' ? 'text-cyan-400 font-mono' : 'text-slate-400 group-hover:text-slate-300'
                  }`}>
                    CANDIDATE
                  </span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer group select-none">
                  <div className="relative flex items-center justify-center">
                    <input
                      type="radio"
                      name="userType"
                      value="recruiter"
                      checked={userType === 'recruiter'}
                      onChange={() => setUserType('recruiter')}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border transition-all duration-200 ${
                      userType === 'recruiter' 
                        ? 'border-cyan-400 bg-cyan-950/30' 
                        : 'border-slate-700 bg-slate-900 group-hover:border-slate-500'
                    }`} />
                    {userType === 'recruiter' && (
                      <div className="absolute w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_#22d3ee]" />
                    )}
                  </div>
                  <span className={`text-sm font-medium tracking-wide transition-colors ${
                    userType === 'recruiter' ? 'text-cyan-400 font-mono' : 'text-slate-400 group-hover:text-slate-300'
                  }`}>
                    RECRUITER
                  </span>
                </label>
              </div>

              <div className="h-[1px] bg-slate-800 my-2" />

              {/* Form Fields Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* First Name */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold tracking-wider text-slate-200/70">FIRST NAME</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 placeholder-white-700 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all font-sans"
                    placeholder="Amit"
                  />
                </div>

                {/* Last Name */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold tracking-wider text-slate-200/70">LAST NAME</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 placeholder-white-700 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
                    placeholder="Patil"
                  />
                </div>

                {/* Email */}
                <div className="flex flex-col gap-1.5 md:col-span-2">
                  <label className="text-xs font-bold tracking-wider text-slate-200/70">EMAIL ADDRESS</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 placeholder-white-700 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
                    placeholder="amit@gmail.com"
                  />
                </div>

                {/* Company Name */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold tracking-wider text-slate-200/70">COMPANY NAME</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 placeholder-white-700 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
                    placeholder="Nexgent"
                  />
                </div>

                {/* Role */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold tracking-wider text-slate-200/70">ROLE / POSITION</label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 placeholder-white-700 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
                    placeholder="CEO"
                  />
                </div>

                {/* Conditional Recruiter Field */}
                {userType === 'recruiter' && (
                  <div className="flex flex-col gap-1.5 md:col-span-2 animate-[fadeIn_0.2s_ease-out]">
                    <label className="text-xs font-mono tracking-wider text-cyan-400">
                      ANNUAL LEADERSHIP HIRING VOLUME
                    </label>
                    <select
                      name="annualLeadershipHiringVolume"
                      value={formData.annualLeadershipHiringVolume}
                      onChange={handleInputChange}
                      required={userType === 'recruiter'}
                      className="bg-slate-950 border border-cyan-900/50 rounded px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 transition-all appearance-none cursor-pointer"
                    >
                      <option value="" disabled className="text-slate-700">Select Volume</option>
                      <option value="1-10" className="bg-slate-900 text-slate-200">1-10 placements</option>
                      <option value="11-24" className="bg-slate-900 text-slate-200">11-24 placements</option>
                      <option value="25-50" className="bg-slate-900 text-slate-200">25-50 placements</option>
                      <option value="50+" className="bg-slate-900 text-slate-200">50+ placements</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full mt-4 bg-cyan-950/40 hover:bg-cyan-500 hover:text-slate-950 text-cyan-400 font-mono text-sm tracking-widest font-bold py-3 px-4 rounded border border-cyan-500/30 hover:border-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.05)] hover:shadow-[0_0_20px_rgba(34,211,238,0.25)] transition-all duration-300 uppercase cursor-pointer"
              >
                Get Access
              </button>
            </form>
          </div>
        </div>
      </div>

      <LandingFooter/>
      </div>
    </>
  );
};